<template>
  <div class="w-full h-full space-y-4">
    <Card>
      <CardHeader>
        <div class="flex items-center gap-2 text-sm text-muted-foreground mb-1">
          <router-link
            to="/whitelabel/themes"
            class="inline-flex items-center gap-1 hover:text-foreground"
          >
            <ChevronLeft class="w-4 h-4" /> All themes
          </router-link>
        </div>
        <CardTitle>
          <input
            v-model="theme.name"
            class="bg-transparent border-0 border-b border-transparent hover:border-border focus:border-primary focus:outline-none w-full max-w-md"
            placeholder="Untitled theme"
          />
        </CardTitle>
        <CardDescription
          >Edit the theme against a live preview of the storefront it renders.</CardDescription
        >
        <CardAction class="flex items-center gap-2">
          <Button variant="outline" size="sm" :disabled="saving || loading" @click="saveTheme">
            <Save class="w-4 h-4" /> {{ saving ? 'Saving…' : 'Save' }}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            class="text-destructive"
            :disabled="deleting || loading"
            @click="pendingDelete = true"
          >
            <Trash2 class="w-4 h-4" />
          </Button>
        </CardAction>
      </CardHeader>
    </Card>

    <div v-if="loading" class="grid gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
      <Skeleton class="h-96" />
      <Skeleton class="h-96" />
    </div>

    <div v-else class="grid gap-4 lg:grid-cols-[360px_minmax(0,1fr)] lg:items-start">
      <!-- Settings sidebar -->
      <Card class="lg:sticky lg:top-4">
        <CardContent class="pt-6 space-y-5">
          <div>
            <div class="flex items-center justify-between mb-2">
              <p class="text-sm font-medium">Colours</p>
              <label class="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                Dark mode
                <Switch :model-value="previewDark" @update:model-value="previewDark = $event" />
              </label>
            </div>
            <div class="grid gap-2">
              <label
                v-for="c in activeColourFields"
                :key="c.key"
                class="flex items-center gap-3 rounded-lg border border-border p-2.5 cursor-pointer hover:bg-muted/50"
              >
                <input
                  type="color"
                  class="size-9 shrink-0 cursor-pointer rounded-md border border-border bg-transparent p-0.5"
                  :value="theme[c.key] || '#000000'"
                  @input="theme[c.key] = $event.target.value"
                  :title="c.label"
                />
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium truncate">{{ c.label }}</p>
                  <p class="text-xs text-muted-foreground font-mono uppercase">
                    {{ theme[c.key] || '-' }}
                  </p>
                </div>
              </label>
            </div>
          </div>

          <label class="flex items-center justify-between rounded-lg border border-border p-2.5">
            <div>
              <p class="text-sm font-medium">Show borders</p>
              <p class="text-xs text-muted-foreground">Outline cards, dividers and inputs.</p>
            </div>
            <Switch
              :model-value="theme.showBorders !== false"
              @update:model-value="theme.showBorders = $event"
            />
          </label>

          <div>
            <p class="text-sm font-medium mb-1">Shadow</p>
            <p class="text-xs text-muted-foreground mb-2">
              Elevation on cards, banners, tiles and video.
            </p>
            <div class="flex gap-2">
              <button
                v-for="o in shadowOptions"
                :key="o.value"
                type="button"
                class="flex-1 rounded-md border border-border py-2 text-xs font-medium"
                :class="
                  (theme.shadow || 'sm') === o.value
                    ? 'ring-2 ring-primary bg-muted'
                    : 'hover:bg-muted/50'
                "
                :style="{ boxShadow: o.css }"
                @click="theme.shadow = o.value"
              >
                {{ o.label }}
              </button>
            </div>
          </div>

          <div>
            <div class="flex items-center justify-between mb-2">
              <p class="text-sm font-medium">Corner radius</p>
              <span class="text-xs text-muted-foreground font-mono">{{ radiusPx }}px</span>
            </div>
            <input
              type="range"
              min="0"
              max="28"
              step="1"
              class="w-full accent-primary cursor-pointer"
              :value="radiusPx"
              @input="radiusPx = Number($event.target.value)"
            />
            <div class="flex items-center gap-2 mt-3">
              <span
                v-for="n in [0, 8, 14, 20]"
                :key="n"
                class="h-8 flex-1 border border-border bg-muted"
                :style="{ borderRadius: n + 'px' }"
                :class="radiusPx === n ? 'ring-2 ring-primary' : ''"
                role="button"
                @click="radiusPx = n"
              />
            </div>
          </div>

          <div>
            <div class="flex items-center justify-between mb-2">
              <p class="text-sm font-medium">Offers per row</p>
            </div>
            <select
              class="h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-sm"
              :value="theme.offersPerRow == null ? '' : String(theme.offersPerRow)"
              @change="
                theme.offersPerRow = $event.target.value ? Number($event.target.value) : null
              "
            >
              <option value="">Auto (responsive)</option>
              <option v-for="n in [1, 2, 3, 4, 5, 6]" :key="n" :value="String(n)">{{ n }}</option>
            </select>
            <p class="text-xs text-muted-foreground mt-1">
              How many offer cards sit in each row on the storefront. Auto fits as many as space
              allows.
            </p>
          </div>

          <div>
            <div class="flex items-center justify-between mb-1">
              <p class="text-sm font-medium">Image overlay</p>
              <button
                v-if="
                  theme.overlayColour ||
                  theme.overlayStrength != null ||
                  theme.overlayDarken != null ||
                  theme.overlayDirection
                "
                type="button"
                class="text-xs text-muted-foreground underline"
                @click="resetOverlay"
              >
                Reset
              </button>
            </div>
            <p class="text-xs text-muted-foreground mb-2">
              The brand-coloured wash over hero banners and offer tiles, so text and buttons stay
              readable.
            </p>
            <div class="grid gap-3">
              <label
                class="flex items-center gap-3 rounded-lg border border-border p-2.5 cursor-pointer hover:bg-muted/50"
              >
                <input
                  type="color"
                  class="size-9 shrink-0 cursor-pointer rounded-md border border-border bg-transparent p-0.5"
                  :value="
                    overlayColourKey === 'darkOverlayColour'
                      ? theme.darkOverlayColour || '#1e293b'
                      : theme.overlayColour || '#1e293b'
                  "
                  @input="theme[overlayColourKey] = $event.target.value"
                  title="Overlay colour"
                />
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium">Overlay colour</p>
                  <p class="text-xs text-muted-foreground font-mono uppercase">
                    {{ theme[overlayColourKey] || 'Default (blue-grey)' }}
                  </p>
                </div>
              </label>
              <div>
                <div class="flex items-center justify-between mb-1">
                  <p class="text-xs font-medium">Tint strength</p>
                  <span class="text-xs text-muted-foreground font-mono"
                    >{{ overlayStrength }}%</span
                  >
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  class="w-full accent-primary cursor-pointer"
                  :value="overlayStrength"
                  @input="theme.overlayStrength = Number($event.target.value)"
                />
              </div>
              <div>
                <div class="flex items-center justify-between mb-1">
                  <p class="text-xs font-medium">Darken towards the edge</p>
                  <span class="text-xs text-muted-foreground font-mono">{{ overlayDarken }}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  class="w-full accent-primary cursor-pointer"
                  :value="overlayDarken"
                  @input="theme.overlayDarken = Number($event.target.value)"
                />
              </div>
              <div class="grid gap-1.5">
                <Label>Direction</Label>
                <select
                  class="h-9 rounded-md border border-border bg-background px-2 text-sm"
                  :value="theme.overlayDirection || 'to bottom'"
                  @change="theme.overlayDirection = $event.target.value"
                >
                  <option v-for="o in overlayDirections" :key="o.value" :value="o.value">
                    {{ o.label }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <p class="text-sm font-medium mb-2">Fonts</p>
            <div class="grid gap-3">
              <div class="grid gap-1.5">
                <Label>Body font</Label>
                <select
                  :value="theme.fontFamily || ''"
                  class="h-9 rounded-md border border-input bg-transparent px-2.5 text-sm"
                  @change="onFontChange('fontFamily', $event.target.value)"
                >
                  <option value="">System default</option>
                  <option v-for="f in customFontNames" :key="'c-' + f" :value="f">
                    {{ f }} (custom)
                  </option>
                  <option v-for="f in libraryFonts" :key="f" :value="f">{{ f }}</option>
                </select>
              </div>
              <div class="grid gap-1.5">
                <Label>Heading font</Label>
                <select
                  :value="theme.headingFontFamily || ''"
                  class="h-9 rounded-md border border-input bg-transparent px-2.5 text-sm"
                  @change="onFontChange('headingFontFamily', $event.target.value)"
                >
                  <option value="">Same as body</option>
                  <option v-for="f in customFontNames" :key="'c-' + f" :value="f">
                    {{ f }} (custom)
                  </option>
                  <option v-for="f in libraryFonts" :key="f" :value="f">{{ f }}</option>
                </select>
              </div>

              <div class="grid gap-1.5">
                <Label>Custom fonts</Label>
                <div v-if="customFontNames.length" class="flex flex-wrap gap-1.5">
                  <span
                    v-for="f in theme.customFonts"
                    :key="f.name"
                    class="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs"
                  >
                    {{ f.name }} <span class="text-muted-foreground">(custom)</span>
                    <button
                      type="button"
                      class="text-muted-foreground hover:text-destructive"
                      :title="`Remove ${f.name}`"
                      @click="removeCustomFont(f.name)"
                    >
                      ✕
                    </button>
                  </span>
                </div>
                <div class="flex gap-2">
                  <input
                    ref="fontFile"
                    type="file"
                    accept=".woff2,.woff,.ttf,.otf"
                    class="hidden"
                    @change="uploadFontFile"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    :disabled="fontBusy"
                    @click="$refs.fontFile.click()"
                  >
                    {{ fontBusy ? 'Working…' : 'Upload font file' }}
                  </Button>
                </div>
                <div class="flex gap-2">
                  <Input
                    v-model="fontImportUrl"
                    class="flex-1"
                    placeholder="https://… (font file or stylesheet)"
                    @keydown.enter="importFontUrl"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    :disabled="fontBusy || !fontImportUrl"
                    @click="importFontUrl"
                  >
                    Import
                  </Button>
                </div>
                <p class="text-xs text-muted-foreground">
                  Upload a font file (woff2/woff/ttf/otf), or paste a URL to a font file or a
                  stylesheet
                </p>
              </div>
              <div class="grid gap-1.5">
                <Label
                  >Font stylesheet URL
                  <span class="text-muted-foreground font-normal">(optional)</span></Label
                >
                <Input
                  v-model="theme.fontUrl"
                  placeholder="https://fonts.googleapis.com/css2?family=Inter…"
                  @change="previewFonts"
                />
                <p class="text-xs text-muted-foreground">
                  Where the fonts load from - paste a Google Fonts link or your own hosted
                  stylesheet, and its fonts are added to the dropdowns. Leave empty to load the
                  chosen fonts from Google Fonts.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Live storefront preview (same component as the live site) -->
      <Card class="overflow-hidden">
        <CardHeader class="border-b">
          <div class="flex items-start justify-between gap-4">
            <div>
              <CardTitle class="text-base">Preview</CardTitle>
              <CardDescription>
                The actual storefront, themed by this theme. Click any element - the navigation,
                hero, category chips, an offer card, the offer header or the footer - to adjust its
                colours directly.
              </CardDescription>
            </div>
            <select
              v-model="previewPage"
              class="h-9 shrink-0 rounded-md border border-input bg-transparent px-2.5 text-sm"
              aria-label="Preview page"
            >
              <option v-for="pg in previewPages" :key="pg.value" :value="pg.value">
                {{ pg.label }}
              </option>
            </select>
          </div>
        </CardHeader>
        <div class="wl-frame">
          <StorefrontPreview
            :config="previewConfig"
            :offers="sampleOffers"
            inspect
            :force-dark="previewDark"
            :preview-page="previewPage"
            @inspect="openElement"
          />
        </div>
      </Card>
    </div>

    <!-- Element editor: opened by clicking a component in the preview. Shows that component
         rendered with the current theme and the colour tokens that style it. -->
    <AlertDialog
      :open="!!elementDef"
      @update:open="
        (v) => {
          if (!v) elementRegion = null
        }
      "
    >
      <AlertDialogContent v-if="elementDef">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {{ elementDef.title }}
            <span v-if="previewDark" class="text-xs font-normal text-muted-foreground align-middle"
              >· dark mode</span
            >
          </AlertDialogTitle>
          <AlertDialogDescription>{{ elementDef.description }}</AlertDialogDescription>
        </AlertDialogHeader>

        <!-- Miniature of the clicked component, live-updated as colours change -->
        <div class="rounded-lg border border-border overflow-hidden" :style="elementVars">
          <div v-if="elementRegion === 'header'" class="el-header">
            <span class="el-brand">{{ theme.name || 'Your brand' }}</span>
            <span class="el-search">Search brands…</span>
            <span class="el-iconbtn">☾</span>
          </div>
          <div v-else-if="elementRegion === 'hero'" class="el-hero">
            <h3>Earn cashback on the brands you love</h3>
            <p>Shop your favourite retailers and get rewarded.</p>
          </div>
          <div v-else-if="elementRegion === 'categories'" class="el-categories">
            <span v-for="c in ['Fashion', 'Travel', 'Electronics']" :key="c" class="el-chip">{{
              c
            }}</span>
          </div>
          <div v-else-if="elementRegion === 'card'" class="el-cardwrap">
            <div class="el-offercard">
              <span class="el-monogram">N</span>
              <span class="el-cardname">Nike</span>
              <span class="el-cardcashback">Up to 5% cashback</span>
            </div>
          </div>
          <div v-else-if="elementRegion === 'offerHeader'" class="el-offerhero">
            <span class="el-offerlogo">N</span>
            <span class="el-offerpill">Earn 5% cashback</span>
          </div>
          <div v-else-if="elementRegion === 'ctaBanner'" class="el-offerhero el-ctabanner">
            <span />
            <span class="el-ctabutton">Download the app</span>
          </div>
          <div v-else-if="elementRegion === 'sectionTitle'" class="el-sectiontitle">
            <strong>Shop our services</strong>
            <span>A short intro under the title.</span>
          </div>
          <div v-else-if="elementRegion === 'ctaTiles'" class="el-ctatiles">
            <div v-for="n in 3" :key="n" class="el-ctatile">
              <div class="el-ctatile-media"><span class="el-ctabutton tiny">Book a ride</span></div>
              <span class="el-ctatile-copy">Tile description.</span>
            </div>
          </div>
          <div v-else-if="elementRegion === 'videoCta'" class="el-videocta">
            <div class="el-videoframe">▶</div>
            <span class="el-ctabutton">Check us out on Trustpilot</span>
          </div>
          <div v-else-if="elementRegion === 'offerTerms'" class="el-offerterms">
            <strong>Offer terms</strong>
            <span>Terms and conditions for this offer.</span>
          </div>
          <div v-else-if="elementRegion === 'footer'" class="el-footer">
            <span>© {{ theme.name || 'Your brand' }}</span>
            <span class="el-footerlinks">Privacy · Terms · Support</span>
          </div>
        </div>

        <div class="grid gap-2">
          <label
            v-for="f in elementFields"
            :key="f.key"
            class="flex items-center gap-3 rounded-lg border border-border p-2.5 cursor-pointer hover:bg-muted/50"
          >
            <input
              type="color"
              class="size-9 shrink-0 cursor-pointer rounded-md border border-border bg-transparent p-0.5"
              :value="componentValue(f)"
              @input="setComponent(f.key, $event.target.value)"
              :title="f.label"
            />
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium truncate">{{ f.label }}</p>
              <p class="text-xs text-muted-foreground font-mono uppercase">
                {{ componentStyle[f.key] || 'Inherits theme' }}
              </p>
            </div>
          </label>

          <div class="grid gap-2 rounded-lg border border-border p-2.5">
            <div class="flex items-center justify-between">
              <p class="text-sm font-medium">Border</p>
              <Switch
                :model-value="componentBorderOn"
                @update:model-value="setComponent('showBorders', $event)"
              />
            </div>
            <label class="flex items-center gap-3" :class="{ 'opacity-40': !componentBorderOn }">
              <input
                type="color"
                class="size-9 shrink-0 cursor-pointer rounded-md border border-border bg-transparent p-0.5"
                :value="componentStyle.borderColour || globalToken('borderColour')"
                :disabled="!componentBorderOn"
                @input="setComponent('borderColour', $event.target.value)"
                title="Border colour"
              />
              <p class="text-xs text-muted-foreground font-mono uppercase">
                {{ componentStyle.borderColour || 'Inherits theme' }}
              </p>
            </label>
          </div>

          <div class="grid gap-1.5 rounded-lg border border-border p-2.5">
            <Label>Shadow</Label>
            <div class="flex gap-1.5">
              <button
                type="button"
                class="rounded-md border border-border px-2 py-1.5 text-xs font-medium"
                :class="
                  !componentStyle.shadow ? 'ring-2 ring-primary bg-muted' : 'hover:bg-muted/50'
                "
                @click="setComponent('shadow', null)"
              >
                Inherit
              </button>
              <button
                v-for="o in shadowOptions"
                :key="'el-' + o.value"
                type="button"
                class="flex-1 rounded-md border border-border py-1.5 text-xs font-medium"
                :class="
                  componentStyle.shadow === o.value
                    ? 'ring-2 ring-primary bg-muted'
                    : 'hover:bg-muted/50'
                "
                :style="{ boxShadow: o.css }"
                @click="setComponent('shadow', o.value)"
              >
                {{ o.label }}
              </button>
            </div>
          </div>

          <div class="grid gap-1.5 rounded-lg border border-border p-2.5">
            <Label>Component font</Label>
            <select
              :value="componentStyle.fontFamily || ''"
              class="h-9 rounded-md border border-input bg-transparent px-2.5 text-sm"
              @change="setComponent('fontFamily', $event.target.value)"
            >
              <option value="">Inherit theme font</option>
              <option v-for="f in customFontNames" :key="'c-' + f" :value="f">
                {{ f }} (custom)
              </option>
              <option v-for="f in libraryFonts" :key="f" :value="f">{{ f }}</option>
            </select>
          </div>
        </div>

        <div class="flex items-center justify-between">
          <p class="text-xs text-muted-foreground">
            Saved on this component only - the rest of the theme is untouched. Applies in light and
            dark mode.
          </p>
          <Button variant="ghost" size="sm" class="shrink-0" @click="resetComponent">Reset</Button>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel @click="elementRegion = null">Cancel</AlertDialogCancel>
          <AlertDialogAction @click="doneElement">Save component</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog
      :open="pendingDelete"
      @update:open="
        (v) => {
          pendingDelete = v
        }
      "
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this theme?</AlertDialogTitle>
          <AlertDialogDescription>
            Any sites using “{{ theme.name }}” will be unlinked and fall back to their own branding.
            This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel @click="pendingDelete = false">Cancel</AlertDialogCancel>
          <AlertDialogAction
            class="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            @click="doDelete"
            >Delete</AlertDialogAction
          >
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
  Button,
  Skeleton,
  StorefrontPreview,
  Input,
  Label,
  Switch,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  toast,
} from '@boogi/ui'
import { Save, Trash2, ChevronLeft } from 'lucide-vue-next'
import { loadThemeFonts, loadGoogleFont, parseFontFamilies } from '@/utils/fonts'

// Light token key → its dark-mode counterpart (primaryColour → darkPrimaryColour).
function darkKey(key) {
  return `dark${key.charAt(0).toUpperCase()}${key.slice(1)}`
}

// Fonts offered in the dropdowns; families parsed from the theme's font URL are added on top.
const CURATED_FONTS = [
  'DM Sans',
  'Inter',
  'Lato',
  'Manrope',
  'Merriweather',
  'Montserrat',
  'Nunito',
  'Open Sans',
  'Playfair Display',
  'Poppins',
  'Raleway',
  'Roboto',
  'Sora',
  'Space Grotesk',
  'Work Sans',
]

// What each clickable preview region exposes in the element editor. Fields are
// ComponentStyle keys - the component's OWN saved values - with `fallback` naming the
// global theme token shown (and inherited) while no override is set.
const ELEMENT_REGIONS = {
  header: {
    title: 'Navigation',
    description: 'The sticky header with your brand, search and theme toggle.',
    fields: [
      { key: 'backgroundColour', label: 'Background', fallback: 'backgroundColour' },
      { key: 'textColour', label: 'Text', fallback: 'textColour' },
    ],
  },
  hero: {
    title: 'Hero banner',
    description: 'The headline banner. Without a hero image it blends these two colours.',
    fields: [
      { key: 'accentColour', label: 'Gradient start', fallback: 'primaryColour' },
      { key: 'secondaryColour', label: 'Gradient end', fallback: 'secondaryColour' },
    ],
  },
  categories: {
    title: 'Category chips',
    description: 'The scrollable category filter row under the hero.',
    fields: [
      { key: 'backgroundColour', label: 'Chip background', fallback: 'backgroundColour' },
      { key: 'textColour', label: 'Chip text', fallback: 'textColour' },
    ],
  },
  card: {
    title: 'Offer card',
    description: 'A brand tile on the offer wall.',
    fields: [
      { key: 'backgroundColour', label: 'Card background', fallback: 'backgroundColour' },
      { key: 'textColour', label: 'Brand name', fallback: 'textColour' },
      { key: 'accentColour', label: 'Cashback accent', fallback: 'primaryColour' },
    ],
  },
  offerHeader: {
    title: 'Offer header',
    description:
      'The banner at the top of a brand page: the tint over the image and the cashback pill.',
    fields: [
      { key: 'accentColour', label: 'Image tint', fallback: 'primaryColour' },
      { key: 'textColour', label: 'Cashback pill text', fallback: 'textColour' },
    ],
  },
  ctaBanner: {
    title: 'CTA banner',
    description: 'The full-width call-to-action banner on an offer page.',
    fields: [
      { key: 'accentColour', label: 'Image tint', fallback: 'primaryColour' },
      { key: 'backgroundColour', label: 'Button', fallback: 'buttonColour' },
      { key: 'textColour', label: 'Button text', fallback: 'buttonTextColour' },
    ],
  },
  sectionTitle: {
    title: 'Section title',
    description: 'The heading and intro above the CTA tiles on an offer page.',
    fields: [{ key: 'textColour', label: 'Text', fallback: 'textColour' }],
  },
  ctaTiles: {
    title: 'CTA tiles',
    description: 'The grid of image tiles on an offer page.',
    fields: [
      { key: 'accentColour', label: 'Image tint', fallback: 'primaryColour' },
      { key: 'backgroundColour', label: 'Tile background', fallback: 'backgroundColour' },
      { key: 'textColour', label: 'Description text', fallback: 'textColour' },
    ],
  },
  videoCta: {
    title: 'Video CTA',
    description: 'The video and the button beneath it on an offer page.',
    fields: [
      { key: 'backgroundColour', label: 'Button', fallback: 'buttonColour' },
      { key: 'textColour', label: 'Button text', fallback: 'buttonTextColour' },
    ],
  },
  offerTerms: {
    title: 'Offer terms',
    description: 'The terms panel at the foot of an offer page.',
    fields: [
      { key: 'backgroundColour', label: 'Panel background', fallback: 'backgroundColour' },
      { key: 'textColour', label: 'Text', fallback: 'textColour' },
    ],
  },
  footer: {
    title: 'Footer',
    description: 'The site footer with your legal and support links.',
    fields: [
      { key: 'backgroundColour', label: 'Background', fallback: 'backgroundColour' },
      { key: 'textColour', label: 'Link text', fallback: 'textColour' },
    ],
  },
}

// Built-in defaults per global token, matching the storefront's own fallbacks.
const TOKEN_DEFAULTS = {
  light: {
    backgroundColour: '#ffffff',
    textColour: '#1f2937',
    primaryColour: '#6b7280',
    secondaryColour: '#374151',
    buttonTextColour: '#ffffff',
    borderColour: '#e5e7eb',
  },
  dark: {
    backgroundColour: '#111827',
    textColour: '#f3f4f6',
    primaryColour: '#6b7280',
    secondaryColour: '#374151',
    buttonTextColour: '#ffffff',
    borderColour: '#374151',
  },
}

// Placeholder imagery for the sample offer page (an inline gradient, so no network needed).
const SAMPLE_IMAGE =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="800" height="500"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#94a3b8"/><stop offset="1" stop-color="#334155"/></linearGradient></defs><rect width="800" height="500" fill="url(#g)"/></svg>'
  )
const SAMPLE_TILE = {
  image: SAMPLE_IMAGE,
  link: 'https://example.com',
  buttonText: 'Book a ride',
  description: 'A short description of this service, set per offer in the admin.',
}

const SAMPLE_OFFERS = [
  {
    id: '1',
    retailerName: 'Nike',
    logo: null,
    highestCashbackValue: 5,
    highestCashbackType: 'PERCENTAGE',
    // The "Offer details" preview opens this offer, so give it every content section.
    description:
      'Nike makes it easy to find the gear you need, from everyday trainers to race day.',
    maxCashback: 150,
    offerTypes: ['ONLINE'],
    usageTypes: ['MULTI_USE'],
    ctaBanner: { image: SAMPLE_IMAGE, link: 'https://example.com', buttonText: 'Download the app' },
    sectionTitle: 'Shop our services',
    sectionDescription: 'Everything this brand offers, in one place.',
    ctaTiles: [SAMPLE_TILE, SAMPLE_TILE, SAMPLE_TILE],
    videoSectionTitle: 'What makes this brand so great?',
    videoSectionDescription: 'A short intro to the video below.',
    videoCta: {
      videoUrl: SAMPLE_IMAGE,
      posterImage: SAMPLE_IMAGE,
      link: 'https://example.com',
      buttonText: 'Check us out on Trustpilot',
    },
    termsAndConditions:
      'Cashback is paid on completed purchases only.\n\nOffer terms are set per offer in the admin and shown here.',
  },
  {
    id: '2',
    retailerName: 'ASOS',
    logo: null,
    highestCashbackValue: 7,
    highestCashbackType: 'PERCENTAGE',
  },
  {
    id: '3',
    retailerName: 'Booking.com',
    logo: null,
    highestCashbackValue: 8,
    highestCashbackType: 'PERCENTAGE',
  },
  {
    id: '4',
    retailerName: 'Apple',
    logo: null,
    highestCashbackValue: 3,
    highestCashbackType: 'PERCENTAGE',
  },
  {
    id: '5',
    retailerName: 'Currys',
    logo: null,
    highestCashbackValue: 4,
    highestCashbackType: 'PERCENTAGE',
  },
  {
    id: '6',
    retailerName: 'Deliveroo',
    logo: null,
    highestCashbackValue: 10,
    highestCashbackType: 'PERCENTAGE',
  },
]

export default {
  name: 'WhitelabelThemeDetail',
  components: {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardAction,
    Button,
    Skeleton,
    StorefrontPreview,
    Input,
    Label,
    Switch,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
    Save,
    Trash2,
    ChevronLeft,
  },
  data() {
    return {
      loading: true,
      saving: false,
      deleting: false,
      pendingDelete: false,
      elementRegion: null,
      previewDark: false,
      previewPage: 'home',
      previewPages: [
        { value: 'home', label: 'Homepage' },
        { value: 'offer', label: 'Offer details' },
      ],
      parsedFamilies: [],
      fontBusy: false,
      fontImportUrl: '',
      theme: {},
      sampleOffers: SAMPLE_OFFERS,
      shadowOptions: [
        { value: 'none', label: 'None', css: 'none' },
        { value: 'xs', label: 'XS', css: '0 1px 2px 0 rgb(0 0 0 / 0.05)' },
        {
          value: 'sm',
          label: 'SM',
          css: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
        {
          value: 'md',
          label: 'MD',
          css: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        },
        {
          value: 'lg',
          label: 'LG',
          css: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        },
        {
          value: 'xl',
          label: 'XL',
          css: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        },
      ],
      overlayDirections: [
        { value: 'to bottom', label: 'Top → bottom' },
        { value: 'to top', label: 'Bottom → top' },
        { value: 'to right', label: 'Left → right' },
        { value: 'to left', label: 'Right → left' },
        { value: '135deg', label: 'Diagonal ↘' },
        { value: '45deg', label: 'Diagonal ↗' },
      ],
      colourFields: [
        { key: 'primaryColour', label: 'Primary' },
        { key: 'secondaryColour', label: 'Secondary' },
        { key: 'backgroundColour', label: 'Background' },
        { key: 'textColour', label: 'Text' },
        { key: 'buttonColour', label: 'Button' },
        { key: 'buttonTextColour', label: 'Button text' },
        { key: 'borderColour', label: 'Border' },
      ],
    }
  },
  computed: {
    // Overlay sliders are 0-100 in the theme; unset falls back to the storefront default (100 = homepage banner gradient).
    overlayStrength() {
      return this.theme.overlayStrength == null ? 100 : this.theme.overlayStrength
    },
    overlayDarken() {
      return this.theme.overlayDarken == null ? 100 : this.theme.overlayDarken
    },
    overlayColourKey() {
      return this.previewDark ? 'darkOverlayColour' : 'overlayColour'
    },
    themeId() {
      return this.$route.params.id
    },
    // Slider works in px; theme.borderRadius is stored as a CSS length like "14px".
    radiusPx: {
      get() {
        const n = parseInt(this.theme.borderRadius, 10)
        return Number.isFinite(n) ? n : 14
      },
      set(v) {
        this.theme = { ...this.theme, borderRadius: `${v}px` }
      },
    },
    // The sidebar edits whichever palette is being previewed (light or dark).
    activeColourFields() {
      if (!this.previewDark) return this.colourFields
      return this.colourFields.map((c) => ({ key: darkKey(c.key), label: c.label }))
    },
    elementDef() {
      return this.elementRegion ? ELEMENT_REGIONS[this.elementRegion] : null
    },
    elementFields() {
      return this.elementDef ? this.elementDef.fields : []
    },
    // The clicked component's own saved style (ComponentStyle on the theme).
    componentStyle() {
      return (this.elementRegion && (this.theme.components || {})[this.elementRegion]) || {}
    },
    // Border toggle for the selected element: on unless explicitly disabled.
    componentBorderOn() {
      return this.componentStyle.showBorders !== false
    },
    customFontNames() {
      return (this.theme.customFonts || []).map((f) => f.name)
    },
    // Font choices: curated list + families the font URL provides + anything already chosen.
    fontOptions() {
      const set = new Set([...CURATED_FONTS, ...this.parsedFamilies])
      ;[this.theme.fontFamily, this.theme.headingFontFamily]
        .filter(Boolean)
        .forEach((f) => set.add(f))
      Object.values(this.theme.components || {}).forEach(
        (c) => c && c.fontFamily && set.add(c.fontFamily)
      )
      return [...set].sort()
    },
    // Library fonts shown after the custom group (custom names never duplicated).
    libraryFonts() {
      const custom = new Set(this.customFontNames.map((n) => n.toLowerCase()))
      return this.fontOptions.filter((f) => !custom.has(f.toLowerCase()))
    },
    // CSS variables driving the miniature component in the element editor: the component's
    // own overrides layered over the mode-appropriate global palette.
    elementVars() {
      const t = this.theme || {}
      const cs = this.componentStyle
      const font = cs.fontFamily || t.fontFamily
      return {
        '--el-primary': cs.accentColour || this.globalToken('primaryColour'),
        '--el-secondary': cs.secondaryColour || this.globalToken('secondaryColour'),
        '--el-bg': cs.backgroundColour || this.globalToken('backgroundColour'),
        '--el-text': cs.textColour || this.globalToken('textColour'),
        '--el-button-text': this.globalToken('buttonTextColour'),
        '--el-radius': t.borderRadius || '14px',
        '--el-border':
          cs.showBorders === false
            ? 'transparent'
            : cs.borderColour || this.globalToken('borderColour'),
        '--el-shadow': (this.shadowOptions.find((o) => o.value === cs.shadow) || {}).css || 'none',
        '--el-font': font ? `'${font}', system-ui, sans-serif` : 'inherit',
      }
    },
    // Map the theme onto a SiteConfigContent the storefront component understands.
    previewConfig() {
      const t = this.theme || {}
      return {
        branding: {
          primaryColour: t.primaryColour,
          secondaryColour: t.secondaryColour,
          backgroundColour: t.backgroundColour,
          textColour: t.textColour,
          buttonColour: t.buttonColour,
          buttonTextColour: t.buttonTextColour,
          borderRadius: t.borderRadius,
          offersPerRow: t.offersPerRow,
          fontFamily: t.fontFamily,
          headingFontFamily: t.headingFontFamily,
          darkPrimaryColour: t.darkPrimaryColour,
          darkSecondaryColour: t.darkSecondaryColour,
          darkBackgroundColour: t.darkBackgroundColour,
          darkTextColour: t.darkTextColour,
          darkButtonColour: t.darkButtonColour,
          darkButtonTextColour: t.darkButtonTextColour,
          overlayColour: t.overlayColour,
          darkOverlayColour: t.darkOverlayColour,
          overlayStrength: t.overlayStrength,
          overlayDarken: t.overlayDarken,
          overlayDirection: t.overlayDirection,
          shadow: t.shadow,
          componentStyles: t.components,
        },
        site: {
          siteTitle: t.name || 'Preview',
          metaDescription: 'Earn cashback on the brands you love.',
          footerText: `© ${t.name || 'Your brand'}`,
          supportEmail: 'help@example.com',
        },
        featureFlags: { search: true, categories: true, darkMode: true },
        offerConfig: { enabledCategories: ['FASHION', 'TRAVEL', 'ELECTRONICS', 'FOOD_DRINK'] },
      }
    },
  },
  created() {
    this.load()
  },
  mounted() {
    document.title = 'Dashboard | Theme'
  },
  methods: {
    base() {
      return localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
    },
    authHeaders(json = true) {
      const h = { Authorization: `${localStorage.getItem('token')}` }
      if (json) h['Content-Type'] = 'application/json'
      return h
    },
    api(path = '') {
      return `${this.base()}/private/api/client/whitelabel/themes/${this.themeId}${path}`
    },
    async load() {
      this.loading = true
      try {
        const res = await this.$apiFetch(this.api(), { method: 'GET', headers: this.authHeaders() })
        if (res.ok) {
          this.theme = await res.json()
          this.previewFonts()
        } else toast.error('Could not load this theme.')
      } catch (e) {
        toast.error('Could not load this theme.')
      } finally {
        this.loading = false
      }
    },
    // Pull the theme's fonts into the dashboard document so the preview renders them,
    // and refresh the dropdown families offered by the font URL.
    onFontChange(key, value) {
      this.theme[key] = value || null
      this.previewFonts()
    },
    async previewFonts() {
      loadThemeFonts(this.theme)
      this.parsedFamilies = await parseFontFamilies(this.theme.fontUrl)
    },
    // ── Custom fonts (uploaded / imported font files) ──
    async fontRequest(path, options, successMessage) {
      this.fontBusy = true
      try {
        const res = await this.$apiFetch(this.api(path), options)
        if (res.ok) {
          this.theme = await res.json()
          this.previewFonts()
          toast.success(successMessage)
        } else {
          const body = await res.text().catch(() => '')
          let message = 'Something went wrong.'
          try {
            message = JSON.parse(body).message || message
          } catch (e) {
            if (body) message = body.slice(0, 140)
          }
          toast.error(message)
        }
      } catch (e) {
        toast.error('Something went wrong.')
      } finally {
        this.fontBusy = false
      }
    },
    async uploadFontFile(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      const form = new FormData()
      form.append('file', file)
      await this.fontRequest(
        '/font',
        { method: 'POST', headers: this.authHeaders(false), body: form },
        'Font uploaded'
      )
    },
    async importFontUrl() {
      const url = (this.fontImportUrl || '').trim()
      if (!url) return
      await this.fontRequest(
        '/font/import',
        { method: 'POST', headers: this.authHeaders(), body: JSON.stringify({ url }) },
        'Fonts imported'
      )
      this.fontImportUrl = ''
    },
    async removeCustomFont(name) {
      await this.fontRequest(
        `/font?name=${encodeURIComponent(name)}`,
        { method: 'DELETE', headers: this.authHeaders() },
        'Font removed'
      )
    },
    // Resolve a global theme token for the previewed mode, with storefront defaults.
    globalToken(key) {
      const t = this.theme || {}
      if (this.previewDark) {
        const dark = t[darkKey(key)]
        if (dark) return dark
        // bg/text have dedicated dark defaults; accents inherit the light value.
        if (key === 'backgroundColour' || key === 'textColour' || key === 'borderColour')
          return TOKEN_DEFAULTS.dark[key]
        return t[key] || TOKEN_DEFAULTS.dark[key]
      }
      return t[key] || TOKEN_DEFAULTS.light[key]
    },
    // ── Element editor (per-component styles) ──
    openElement(region) {
      if (!this.theme.components) this.theme.components = {}
      if (!this.theme.components[region]) this.theme.components[region] = {}
      this.elementRegion = region
    },
    componentValue(f) {
      return this.componentStyle[f.key] || this.globalToken(f.fallback)
    },
    setComponent(key, value) {
      // Booleans (e.g. showBorders) must persist false, not collapse to null (= inherit).
      this.theme.components[this.elementRegion][key] =
        typeof value === 'boolean' ? value : value || null
      if (key === 'fontFamily' && value) loadGoogleFont(value)
    },
    resetComponent() {
      if (this.elementRegion) this.theme.components[this.elementRegion] = {}
    },
    // Closing the element editor persists the component's own styles on the theme.
    doneElement() {
      this.elementRegion = null
      this.saveTheme()
    },
    resetOverlay() {
      this.theme = {
        ...this.theme,
        overlayColour: null,
        darkOverlayColour: null,
        overlayStrength: null,
        overlayDarken: null,
        overlayDirection: null,
      }
    },
    async saveTheme() {
      this.saving = true
      try {
        const res = await this.$apiFetch(this.api(), {
          method: 'PUT',
          headers: this.authHeaders(),
          body: JSON.stringify(this.theme),
        })
        if (res.ok) {
          this.theme = await res.json()
          toast.success('Theme saved')
        } else toast.error('Could not save theme.')
      } catch (e) {
        toast.error('Could not save theme.')
      } finally {
        this.saving = false
      }
    },
    async doDelete() {
      this.pendingDelete = false
      this.deleting = true
      try {
        const res = await this.$apiFetch(this.api(), {
          method: 'DELETE',
          headers: this.authHeaders(),
        })
        if (res.ok) {
          toast.success('Theme deleted')
          this.$router.push('/whitelabel/themes')
        } else toast.error('Could not delete theme.')
      } catch (e) {
        toast.error('Could not delete theme.')
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
/* A device-like frame so the full-bleed storefront preview scrolls within the card. */
.wl-frame {
  height: 640px;
  overflow: auto;
}

/* Miniature components in the element editor, driven by --el-* variables. */
.el-videocta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 14px;
}
.el-videoframe {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70%;
  height: 70px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--el-primary) 20%, #000);
  color: #fff;
}
.el-offerterms {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  background: var(--el-bg);
  color: var(--el-text);
  font-family: var(--el-font, inherit);
  font-size: 0.85rem;
}
.el-ctabutton {
  padding: 8px 16px;
  border-radius: 999px;
  background: var(--el-bg);
  color: var(--el-text);
  font-weight: 700;
  font-size: 0.85rem;
}
.el-ctabutton.tiny {
  padding: 4px 10px;
  font-size: 0.7rem;
}
.el-sectiontitle {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 18px;
  color: var(--el-text);
  font-family: var(--el-font, inherit);
}
.el-ctatiles {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 12px;
}
.el-ctatile {
  border-radius: 10px;
  overflow: hidden;
  background: var(--el-bg);
}
.el-ctatile-media {
  position: relative;
  height: 64px;
  background: linear-gradient(
    to bottom,
    color-mix(in srgb, var(--el-primary) 40%, #9ca3af),
    color-mix(in srgb, var(--el-primary) 55%, #000)
  );
}
.el-ctatile-media .el-ctabutton {
  position: absolute;
  right: 6px;
  bottom: 6px;
  background: var(--el-primary);
  color: #fff;
}
.el-ctatile-copy {
  display: block;
  padding: 8px;
  font-size: 0.7rem;
  color: var(--el-text);
}
.el-offerhero {
  position: relative;
  height: 110px;
  padding: 14px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  background: linear-gradient(
    to bottom,
    color-mix(in srgb, var(--el-primary) 45%, #9ca3af),
    color-mix(in srgb, var(--el-primary) 45%, #000)
  );
  font-family: var(--el-font, inherit);
}
.el-offerlogo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff;
  color: var(--el-primary);
  font-weight: 800;
}
.el-offerpill {
  padding: 8px 14px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--el-primary) 12%, #fff);
  color: var(--el-text);
  font-weight: 600;
  font-size: 0.85rem;
}
.el-header,
.el-hero,
.el-chip,
.el-offercard,
.el-footer {
  font-family: var(--el-font, inherit);
}
.el-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--el-bg);
  color: var(--el-text);
}
.el-brand {
  font-weight: 700;
}
.el-search {
  flex: 1;
  padding: 6px 14px;
  border: 1px solid color-mix(in srgb, var(--el-text) 18%, transparent);
  border-radius: calc(var(--el-radius, 14px) * 2.5);
  font-size: 0.8rem;
  opacity: 0.65;
}
.el-iconbtn {
  padding: 4px 9px;
  border: 1px solid color-mix(in srgb, var(--el-text) 18%, transparent);
  border-radius: var(--el-radius);
}
.el-hero {
  padding: 24px 20px;
  background: linear-gradient(120deg, var(--el-primary), var(--el-secondary));
  color: #fff;
}
.el-hero h3 {
  margin: 0 0 4px;
  font-size: 1.1rem;
  font-weight: 800;
}
.el-hero p {
  margin: 0;
  font-size: 0.85rem;
  opacity: 0.92;
}
.el-categories {
  display: flex;
  gap: 8px;
  padding: 14px 16px;
  background: color-mix(in srgb, var(--el-bg) 92%, var(--el-text));
}
.el-chip {
  padding: 6px 14px;
  border-radius: calc(var(--el-radius, 14px) * 2.5);
  border: 1px solid color-mix(in srgb, var(--el-text) 18%, transparent);
  background: var(--el-bg);
  color: var(--el-text);
  font-size: 0.8rem;
  font-weight: 600;
}
.el-cardwrap {
  padding: 16px;
  background: color-mix(in srgb, var(--el-bg) 92%, var(--el-text));
}
.el-offercard {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 200px;
  padding: 14px;
  background: var(--el-bg);
  border: 1px solid color-mix(in srgb, var(--el-text) 14%, transparent);
  border-radius: var(--el-radius);
}
.el-monogram {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--el-radius);
  background: var(--el-primary);
  color: var(--el-button-text);
  font-weight: 800;
}
.el-cardname {
  font-weight: 700;
  color: var(--el-text);
}
.el-cardcashback {
  color: var(--el-primary);
  font-weight: 700;
  font-size: 0.85rem;
}
.el-footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: var(--el-bg);
  color: var(--el-text);
  font-size: 0.85rem;
}
.el-footerlinks {
  opacity: 0.75;
}
</style>
